"use client";

import { Fragment, useEffect, useState } from "react";
import Image from "next/image";
import Images from "@/app/assets/shared";
import { techStacks } from "../tech";

const SLIDE_INTERVAL = 5000;

interface TechSliderItemProps {
  title: string[];
  subtitle: string;
  description: string;
}

export const TechStackSlider = ({
  title,
  subtitle,
  description,
}: TechSliderItemProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const totalStacks = techStacks.length;

  useEffect(() => {
    if (totalStacks <= 1) return;

    const interval = window.setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % totalStacks);
    }, SLIDE_INTERVAL);

    return () => window.clearInterval(interval);
  }, [totalStacks]);

  if (!totalStacks) {
    return null;
  }

  const normalizedIndex = activeIndex % totalStacks;
  return (
    <section className="relative z-2 overflow-hidden bg-cover bg-center">
      <div className="absolute inset-0 -z-10">
        <Image
          src={Images?.serviceCtaBg}
          alt="background"
          fill
          className="object-cover"
          sizes="100vw"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#0C41B2] via-[#0C41B2]/70 to-transparent" />

      <div
        className="pointer-events-none absolute inset-0 flex items-center justify-center"
        aria-hidden="true"
      >
        <div className="relative h-[80%] aspect-[1/1]">
          <div className="absolute inset-0 rounded-full border border-[rgba(255,255,255,0.2)]"></div>
          <div className="absolute inset-0 rounded-full overflow-hidden">
            <div className="absolute inset-0 origin-center animate-[spin_4.5s_linear_infinite]">
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[140%] h-1/2 bg-gradient-to-t from-[#0C41B2] via-[#0C41B2]/70 to-transparent"></div>
            </div>
          </div>

          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[60%] aspect-[1/1]">
            <div className="absolute inset-0 rounded-full border border-[rgba(255,255,255,0.2)]"></div>
            <div className="absolute inset-0 rounded-full overflow-hidden">
              <div className="absolute inset-0 origin-center animate-[spin_4.5s_linear_infinite]">
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[140%] h-1/2 bg-gradient-to-t from-[#0C41B2] via-[#0C41B2]/70 to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="relative z-10 max-w-[2500px] mx-auto w-[calc(100%_-_10%)] py-[80px] md:py-[120px] lg:py-[150px]">
        <div className="flex flex-col gap-8 md:gap-10 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex-1 w-full text-white lg:max-w-[820px] xl:max-w-[880px]">
            <p className="uppercase tracking-[0.12em] text-[length:var(--primarybtn)] text-white/70 text-center lg:text-left">
              {subtitle}
            </p>
            <h2 className="mt-3 clash-font font-medium leading-[100%] text-[length:var(--section-title)] text-center lg:text-left">
              {title?.map((line, index) => (
                <Fragment key={`hero-line-${index}`}>
                  {line}
                  {index !== title.length - 1 && (
                    <br className="hidden lg:block" />
                  )}
                </Fragment>
              ))}
            </h2>
            <p className="mt-6 text-white/80 max-w-[70ch] text-[length:var(--hero-para)] leading-[150%] mx-auto lg:mx-0 text-center lg:text-left">
              {description}
            </p>
          </div>

          <div className="w-full lg:max-w-[560px] relative min-h-[520px]">
            {techStacks?.map((stack, index) => {
              const isActive = index === normalizedIndex;
              return (
                <article
                  key={stack.id}
                  className={`absolute inset-0 transition-all duration-700 ease-out ${
                    isActive
                      ? "opacity-100 translate-y-0 scale-100 pointer-events-auto"
                      : "opacity-0 translate-y-4 scale-[0.98] pointer-events-none"
                  }`}
                  aria-hidden={!isActive}
                >
                  <div className="bg-white shadow-[0_10px_50px_rgba(12,65,178,0.2)] p-7 sm:p-8 md:p-10 rounded-2xl">
                    <div className="flex items-center gap-4 sm:gap-5">
                      <Image
                        src={stack.graphic}
                        alt={stack.title}
                        className="w-[96px] h-[96px] object-contain"
                        sizes="(max-width: 1024px) 100vw, 50vw"
                        fetchPriority="high"
                        width={96}
                        height={96}
                      />
                      <div className="text-[#0B0F17] font-medium text-[length:var(--industry-title)] leading-[100%] clash-font">
                        {stack.title}
                      </div>
                    </div>

                    <div className="mt-8 grid gap-5 sm:gap-6">
                      {stack.features?.map((feature) => (
                        <div
                          key={`${stack.id}-${feature.label}`}
                          className="flex items-start gap-3 sm:gap-4"
                        >
                          <span className="inline-flex items-center justify-center w-10 h-10 rounded-md bg-[#0C41B2]/10">
                            <Image
                              src={feature.icon}
                              alt={feature.label}
                              className="w-6 h-6"
                              width={24}
                              height={24}
                              sizes="(max-width: 1024px) 100vw, 50vw"
                              fetchPriority="high"
                            />
                          </span>
                          <div>
                            <div className="text-[#0B0F17] font-medium">
                              {feature.label}
                            </div>
                            <div className="text-[#0B0F17]/70">
                              {feature.value}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <Image
                    src={Images?.career.floatingText}
                    alt="Start building with top talent"
                    className="hidden lg:block absolute translate-x-[calc(-100%_+_0px)] translate-y-[calc(100%_-_100px)] left-[0%] bottom-[0px] w-[220px]"
                    width={220}
                    height={220}
                    sizes="(max-width: 1024px) 100vw, 50vw"
                    fetchPriority="high"
                  />
                </article>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TechStackSlider;
